// src/pages/ListIngressos.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { EVENTOS, EVENTO_ATUAL } from '../config/event';
import { confirmEmailOtp, friendlyAuthError, startEmailOtp } from '../config/auth';
import QrValidator from '../components/QrValidator';
import './LandingPage.css';
import './ListIngressos.css';

// Itens antigos (Brasilidades) não têm evento_id gravado.
const LEGADO_EVENTO_ID = 'brasilidades-2025';
const API_BASE = 'https://ta3m3foyci.execute-api.sa-east-1.amazonaws.com';
const TOKEN_KEY = 'era_admin_id_token';

type Step = 'email' | 'code' | 'authed';

const ListIngressos: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [session, setSession] = useState('');
  const [idToken, setIdToken] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [eventoId, setEventoId] = useState<string>(EVENTO_ATUAL);
  const [ingressos, setIngressos] = useState<any[]>([]);
  const [links, setLinks] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState<{ [key: string]: boolean }>({});
  const [searched, setSearched] = useState(false);
  const evento = EVENTOS[eventoId];
  const TOTAL_SEATS = evento.capacidade;
  const COTA_MEIA = evento.cotaMeia;

  const logout = useCallback((message?: string) => {
    sessionStorage.removeItem(TOKEN_KEY);
    setIdToken('');
    setSession('');
    setOtpCode('');
    setIngressos([]);
    setSearched(false);
    setStep('email');
    setError(message || '');
  }, []);

  const fetchLista = useCallback(async (token: string, evento: string) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ evento_id: evento }),
      });

      if (response.status === 401 || response.status === 403) {
        logout('Sessão expirada. Entre novamente.');
        return;
      }
      if (!response.ok) {
        setIngressos([]);
        setError('Erro ao buscar ingressos. Tente novamente.');
        return;
      }

      const data = await response.json();
      // Compat: filtra no cliente caso o backend retorne outros eventos.
      const filtrados = Array.isArray(data)
        ? data.filter((i: any) => (i.evento_id || LEGADO_EVENTO_ID) === evento)
        : [];
      setIngressos(filtrados);
      setSearched(true);
      if (filtrados.length === 0) {
        setError('Nenhum ingresso encontrado para este evento');
      }
    } catch (err) {
      console.error('Error fetching ingressos:', err);
      setError('Erro ao buscar ingressos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  // Sessão persistida: valida chamando a API (401 limpa e volta ao login).
  useEffect(() => {
    const stored = sessionStorage.getItem(TOKEN_KEY);
    if (stored) {
      setIdToken(stored);
      setStep('authed');
      fetchLista(stored, EVENTO_ATUAL);
    }
  }, [fetchLista]);

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setAuthLoading(true);
    setError('');
    try {
      const s = await startEmailOtp(email);
      setSession(s);
      setOtpCode('');
      setStep('code');
    } catch (err) {
      console.error('Error starting OTP:', err);
      setError(friendlyAuthError(err));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleConfirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpCode.trim()) return;
    setAuthLoading(true);
    setError('');
    try {
      const token = await confirmEmailOtp(email, otpCode, session);
      sessionStorage.setItem(TOKEN_KEY, token);
      setIdToken(token);
      setStep('authed');
      fetchLista(token, eventoId);
    } catch (err) {
      console.error('Error confirming OTP:', err);
      setError(friendlyAuthError(err));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRefresh = (event: React.FormEvent) => {
    event.preventDefault();
    if (idToken) fetchLista(idToken, eventoId);
  };

  const handleResend = async (ingresso: any) => {
    const { nome, email: emailIngresso, documento, tipo_ingresso, documento_meia_entrada, ticket_number, evento_id } = ingresso;
    const key = String(ticket_number);
    setResending((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`${API_BASE}/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          nome,
          email: emailIngresso,
          documento,
          tipo_ingresso,
          documento_meia_entrada,
          ticket_number,
          evento_id: evento_id || eventoId,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        logout('Sessão expirada. Entre novamente.');
        return;
      }
      if (!response.ok) {
        setError('Erro ao reenviar ingresso');
        return;
      }

      const data = await response.json();
      setLinks((prevLinks) => ({
        ...prevLinks,
        [key]: data.url,
      }));
    } catch (err) {
      console.error('Error resending ingresso:', err);
      setError('Erro ao reenviar ingresso. Tente novamente.');
    } finally {
      setResending((prev) => ({ ...prev, [key]: false }));
    }
  };

  const meiasVendidas = ingressos.filter(i => String(i.tipo_ingresso).toLowerCase() === 'meia').length;
  const confirmados = ingressos.filter(i => i.payment_approved).length;

  return (
    <div className="era-landing era-admin">
      <header className="era-header">
        <div className="era-header-inner">
          <a href="https://espacoera.store/" className="era-brand-link" title="Espaço ERA — Página Principal">
            <img src="/logo-era.jpg" alt="Logo Espaço ERA" className="era-header-logo" />
          </a>
          <button
            type="button"
            className="era-nav-toggle"
            aria-expanded={isMenuOpen}
            aria-controls="era-nav-principal"
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            {isMenuOpen ? 'Fechar' : 'Menu'}
          </button>
          <nav id="era-nav-principal" className={`era-nav${isMenuOpen ? ' is-open' : ''}`} aria-label="Navegação principal">
            <a href="/" className="era-nav-link" onClick={() => setIsMenuOpen(false)}>Voltar ao site</a>
          </nav>
        </div>
      </header>

      <main className="era-admin-main">
        <div className="era-wrap">
          <p className="era-folio">{evento.titulo} — {evento.data}</p>
          <h1 className="era-h2">Listagem de ingressos</h1>

          {step !== 'authed' && (
            <p className="era-admin-sub">
              Área interna. Informe seu email para receber um código de acesso.
            </p>
          )}
          {step === 'authed' && (
            <p className="era-admin-sub">
              Logado como {email || 'administrador'} —{' '}
              <button type="button" className="era-cta-line" onClick={() => logout()}>
                Sair
              </button>
            </p>
          )}

          {step === 'email' && (
            <form className="era-admin-form era-admin-form-login" onSubmit={handleSendCode}>
              <div className="era-admin-field">
                <label className="era-admin-label" htmlFor="adminEmail">Email</label>
                <input
                  id="adminEmail"
                  className="era-admin-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com"
                  autoComplete="email"
                  required
                />
              </div>
              <button type="submit" className="era-primary-btn era-admin-submit" disabled={authLoading}>
                {authLoading ? (
                  <span className="btn-loading-state">
                    <span className="spinner-era" aria-hidden="true" />
                    Enviando
                  </span>
                ) : 'Receber código'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form className="era-admin-form era-admin-form-login" onSubmit={handleConfirmCode}>
              <p className="era-admin-sub" role="status">
                Código enviado para {email}. Vale por 15 minutos — use sempre o
                último recebido.{' '}
                <button type="button" className="era-cta-line" onClick={() => { setStep('email'); setError(''); }}>
                  Trocar email
                </button>
              </p>
              <div className="era-admin-field">
                <label className="era-admin-label" htmlFor="adminOtp">Código</label>
                <input
                  id="adminOtp"
                  className="era-admin-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="000000"
                  required
                />
              </div>
              <button type="submit" className="era-primary-btn era-admin-submit" disabled={authLoading}>
                {authLoading ? (
                  <span className="btn-loading-state">
                    <span className="spinner-era" aria-hidden="true" />
                    Verificando
                  </span>
                ) : 'Entrar'}
              </button>
              <button type="button" className="era-cta-line" onClick={handleSendCode} disabled={authLoading}>
                Reenviar código
              </button>
            </form>
          )}

          {step === 'authed' && (
            <form className="era-admin-form" onSubmit={handleRefresh}>
              <div className="era-admin-field">
                <label className="era-admin-label" htmlFor="adminEvento">Evento</label>
                <select
                  id="adminEvento"
                  className="era-admin-input"
                  value={eventoId}
                  onChange={(e) => { setEventoId(e.target.value); setIngressos([]); setSearched(false); setError(''); }}
                  aria-label="Evento"
                >
                  {Object.values(EVENTOS).map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.titulo} — {ev.data}</option>
                  ))}
                </select>
              </div>
              <button type="submit" className="era-primary-btn era-admin-submit" disabled={isLoading}>
                {isLoading ? (
                  <span className="btn-loading-state">
                    <span className="spinner-era" aria-hidden="true" />
                    Buscando
                  </span>
                ) : 'Atualizar'}
              </button>
            </form>
          )}

          {error && (
            <p className="era-admin-error" role="alert">{error}</p>
          )}

          {step === 'authed' && ingressos.length > 0 && (
            <dl className="era-admin-stats" aria-live="polite">
              <div className="era-admin-stat">
                <dt className="era-admin-stat-label">Ingressos vendidos</dt>
                <dd className="era-admin-stat-value">{ingressos.length}</dd>
              </div>
              <div className="era-admin-stat">
                <dt className="era-admin-stat-label">Pagamentos confirmados</dt>
                <dd className="era-admin-stat-value">{confirmados}</dd>
              </div>
              <div className="era-admin-stat">
                <dt className="era-admin-stat-label">Meias vendidas</dt>
                <dd className="era-admin-stat-value">{meiasVendidas}/{COTA_MEIA}</dd>
              </div>
              <div className="era-admin-stat">
                <dt className="era-admin-stat-label">Ingressos disponíveis</dt>
                <dd className="era-admin-stat-value">{Math.max(TOTAL_SEATS - ingressos.length, 0)}</dd>
              </div>
              <div className="era-admin-stat">
                <dt className="era-admin-stat-label">Ocupação</dt>
                <dd className="era-admin-stat-value">{((ingressos.length / TOTAL_SEATS) * 100).toFixed(2)}%</dd>
              </div>
            </dl>
          )}

          {step === 'authed' && ingressos.length > 0 && (
            <QrValidator ingressos={ingressos} />
          )}

          {step === 'authed' && ingressos.length > 0 && (
            <div className="era-tabela-wrap era-admin-tabela-wrap">
              <table className="era-tabela era-admin-tabela">
                <caption className="era-visually-hidden">
                  Ingressos de {evento.titulo}. {ingressos.length} registros, {confirmados} com pagamento aprovado.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Ticket</th>
                    <th scope="col">Nome</th>
                    <th scope="col">Documento</th>
                    <th scope="col">Email</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Pagamento</th>
                    <th scope="col">Reenvio</th>
                    <th scope="col">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {ingressos.map((ingresso, index) => {
                    const key = String(ingresso.ticket_number);
                    return (
                      <tr key={key}>
                        <td>{index + 1}</td>
                        <td>{ingresso.ticket_number}</td>
                        <td>{ingresso.nome}</td>
                        <td>{ingresso.documento}</td>
                        <td>{ingresso.email}</td>
                        <td>{ingresso.tipo_ingresso}</td>
                        <td>
                          <span className={`era-admin-badge ${ingresso.payment_approved ? 'approved' : 'pending'}`}>
                            {ingresso.payment_approved ? 'Aprovado' : 'Pendente'}
                          </span>
                        </td>
                        <td>
                          {ingresso.payment_approved ? (
                            <button
                              type="button"
                              className="era-admin-resend"
                              onClick={() => handleResend(ingresso)}
                              disabled={!!resending[key]}
                            >
                              {resending[key] ? 'Gerando' : 'Obter ingresso'}
                            </button>
                          ) : (
                            <span className="era-admin-not-approved">Pagamento não aprovado</span>
                          )}
                        </td>
                        <td>
                          {links[key] && (
                            <a href={links[key]} target="_blank" rel="noopener noreferrer" className="era-cta-line">
                              PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {step === 'authed' && searched && !isLoading && ingressos.length === 0 && !error && (
            <p className="era-admin-sub" role="status">Nenhum ingresso para exibir.</p>
          )}
        </div>
      </main>

      <div className="era-meandro" aria-hidden="true" />

      <footer className="era-footer">
        <div className="era-wrap era-footer-grid">
          <div className="era-footer-col">
            <a href="/">Voltar ao site</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ListIngressos;
